import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown, Inbox } from "lucide-react";
import styles from "./DataTable.module.css";

const DataTable = ({
  columns = [],
  data = [],
  isLoading = false,
  emptyMessage = "No records found",
  headerActions,
  pagination,
  onRowClick,
  rowKey = "id",
  className = "",
}) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortKey(null);
        setSortDir("asc");
      }
    } else {
      setSortKey(col.key);
      setSortDir("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (typeof aVal === "string") {
        return sortDir === "asc"
          ? aVal.localeCompare(String(bVal))
          : String(bVal).localeCompare(aVal);
      }
      return sortDir === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [data, sortKey, sortDir]);

  return (
    <div className={`${styles.tableContainer} ${className}`.trim()}>
      {headerActions && <div className={styles.tableHeaderSlot}>{headerActions}</div>}

      <div className={styles.scrollArea}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              {columns.map((col) => {
                const isSorted = sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    className={`${styles.th} ${col.sortable ? styles.sortable : ""}`}
                    onClick={() => handleSort(col)}
                  >
                    <div className={styles.thContent}>
                      <span>{col.label}</span>
                      {col.sortable && (
                        <span>
                          {isSorted ? (
                            sortDir === "asc" ? (
                              <ArrowUp size={13} />
                            ) : (
                              <ArrowDown size={13} />
                            )
                          ) : (
                            <ArrowUpDown size={12} opacity={0.4} />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className={styles.tbody}>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className={styles.tr}>
                  <td colSpan={columns.length} className={styles.td}>
                    <div className={styles.skeletonRow} />
                  </td>
                </tr>
              ))
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.td}>
                  <div className={styles.emptyBox}>
                    <Inbox size={32} opacity={0.4} />
                    <span>{emptyMessage}</span>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, idx) => {
                const key = row[rowKey] || idx;
                const isClickable = typeof onRowClick === "function";

                return (
                  <tr
                    key={key}
                    className={`${styles.tr} ${isClickable ? styles.clickable : ""}`}
                    onClick={() => isClickable && onRowClick(row)}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={styles.td}>
                        {typeof col.render === "function"
                          ? col.render(row[col.key], row, idx)
                          : row[col.key] !== undefined && row[col.key] !== null
                          ? String(row[col.key])
                          : "—"}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className={styles.paginationBar}>
          <div>
            Showing{" "}
            <strong>
              {(pagination.currentPage - 1) * pagination.pageSize + 1}
            </strong>{" "}
            to{" "}
            <strong>
              {Math.min(
                pagination.currentPage * pagination.pageSize,
                pagination.totalCount || 0
              )}
            </strong>{" "}
            of <strong>{pagination.totalCount || 0}</strong> entries
          </div>

          <div className={styles.paginationControls}>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => pagination.onPageChange(1)}
              disabled={pagination.currentPage <= 1}
              aria-label="First page"
            >
              <ChevronsLeft size={14} />
            </button>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>

            <span style={{ margin: "0 6px", fontWeight: 600 }}>
              Page {pagination.currentPage} of {pagination.totalPages || 1}
            </span>

            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= (pagination.totalPages || 1)}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => pagination.onPageChange(pagination.totalPages)}
              disabled={pagination.currentPage >= (pagination.totalPages || 1)}
              aria-label="Last page"
            >
              <ChevronsRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
